import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, BarChart3, TrendingUp, Target, Flame, Star,
  Calendar, Award, Zap, ChevronRight, Lock,
} from 'lucide-react';

// ─── Mock Data ──────────────────────────────────────────────────────

const LEVEL_CONFIG = [
  { level: 1, name: 'Rookie', minXP: 0, color: 'gray' },
  { level: 5, name: 'Bronze I', minXP: 500, color: 'orange' },
  { level: 8, name: 'Bronze II', minXP: 1200, color: 'orange' },
  { level: 10, name: 'Silver I', minXP: 2000, color: 'gray' },
  { level: 12, name: 'Silver II', minXP: 3200, color: 'gray' },
  { level: 14, name: 'Silver III', minXP: 4800, color: 'gray' },
  { level: 16, name: 'Gold I', minXP: 6800, color: 'amber' },
  { level: 20, name: 'Gold II', minXP: 9200, color: 'amber' },
  { level: 25, name: 'Diamond', minXP: 15000, color: 'cyan' },
];

const CURRENT_LEVEL = 14;
const CURRENT_XP = 5340;
const NEXT_LEVEL_XP = 6800;
const CURRENT_TIER = 'Silver III';

const WEEKLY_STATS = [
  { week: 'W1 Feb', upsellRate: 58, greeting: 82, drinks: 4, shifts: 5 },
  { week: 'W2 Feb', upsellRate: 62, greeting: 85, drinks: 5, shifts: 5 },
  { week: 'W3 Feb', upsellRate: 60, greeting: 88, drinks: 6, shifts: 4 },
  { week: 'W4 Feb', upsellRate: 65, greeting: 87, drinks: 5, shifts: 5 },
  { week: 'W1 Mar', upsellRate: 68, greeting: 90, drinks: 7, shifts: 5 },
  { week: 'W2 Mar', upsellRate: 72, greeting: 92, drinks: 8, shifts: 5 },
];

interface PersonalGoal {
  name: string;
  current: number;
  target: number;
  unit: string;
  icon: React.ComponentType<{ className?: string }>;
}

const GOALS: PersonalGoal[] = [
  { name: 'Weekly Upsell Rate', current: 72, target: 80, unit: '%', icon: Target },
  { name: 'Greeting Score', current: 92, target: 95, unit: '%', icon: Star },
  { name: 'Daily Streak Best', current: 7, target: 10, unit: '', icon: Flame },
  { name: 'Drink Promos / Week', current: 8, target: 12, unit: '', icon: Zap },
];

interface Milestone {
  name: string;
  description: string;
  date: string;
  icon: React.ComponentType<{ className?: string }>;
  unlocked: boolean;
}

const MILESTONES: Milestone[] = [
  { name: 'First Upsell', description: 'Completed your first successful upsell', date: 'Jan 15', icon: Zap, unlocked: true },
  { name: 'Week Warrior', description: '5 consecutive days with 60%+ upsell rate', date: 'Jan 28', icon: Calendar, unlocked: true },
  { name: 'Silver Tier', description: 'Reached Silver I ranking', date: 'Feb 8', icon: Award, unlocked: true },
  { name: 'Streak Master', description: '7+ interaction streak in one shift', date: 'Mar 14', icon: Flame, unlocked: true },
  { name: 'Gold Tier', description: 'Reach Gold I ranking', date: '—', icon: Star, unlocked: false },
  { name: 'Century Club', description: '100 successful upsells', date: '—', icon: Target, unlocked: false },
];

// ─── Component ──────────────────────────────────────────────────────

const MyStats: React.FC = () => {
  const navigate = useNavigate();
  const [selectedMetric, setSelectedMetric] = useState<'upsellRate' | 'greeting' | 'drinks'>('upsellRate');

  const xpProgress = ((CURRENT_XP - 4800) / (NEXT_LEVEL_XP - 4800)) * 100;
  const maxStat = Math.max(...WEEKLY_STATS.map(w => w[selectedMetric]));

  const metricLabels = {
    upsellRate: 'Upsell Rate (%)',
    greeting: 'Greeting Score (%)',
    drinks: 'Drink Promos',
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button onClick={() => navigate('/restaurant/mockups')} className="flex items-center gap-1.5 text-blue-200 hover:text-white text-sm mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Hub
          </button>
          <h1 className="text-2xl font-bold text-white">My Stats</h1>
          <p className="text-blue-200 text-sm">Your personal performance journey</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Level card */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              {/* Level badge */}
              <div className="relative">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">{CURRENT_LEVEL}</span>
                </div>
                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 bg-gray-600 text-white text-[9px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap">
                  {CURRENT_TIER}
                </div>
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Level {CURRENT_LEVEL}</h2>
                <p className="text-sm text-gray-500">{CURRENT_XP.toLocaleString()} / {NEXT_LEVEL_XP.toLocaleString()} XP to Level {CURRENT_LEVEL + 1}</p>
              </div>
            </div>
            <div className="text-right hidden sm:block">
              <div className="text-xs text-gray-400">Next tier</div>
              <div className="text-sm font-medium text-amber-500 flex items-center gap-1">
                Gold I <ChevronRight className="w-3.5 h-3.5" />
              </div>
            </div>
          </div>

          {/* XP bar */}
          <div className="relative">
            <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full transition-all duration-1000"
                style={{ width: `${xpProgress}%` }}
              />
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-[10px] text-gray-400">Lvl {CURRENT_LEVEL}</span>
              <span className="text-[10px] text-gray-400">{NEXT_LEVEL_XP - CURRENT_XP} XP to go</span>
              <span className="text-[10px] text-gray-400">Lvl {CURRENT_LEVEL + 1}</span>
            </div>
          </div>

          {/* Level perks */}
          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
            <div className="text-xs font-medium text-blue-800 mb-1">Level {CURRENT_LEVEL + 1} unlocks:</div>
            <div className="flex items-center gap-4 text-xs text-blue-600">
              <span>+5% bonus point multiplier</span>
              <span>Priority scheduling access</span>
              <span>Gold tier badge</span>
            </div>
          </div>
        </div>

        {/* Trend chart */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-gray-900">Your Trend</h2>
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
              {(['upsellRate', 'greeting', 'drinks'] as const).map((m) => (
                <button
                  key={m}
                  onClick={() => setSelectedMetric(m)}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    selectedMetric === m ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {m === 'upsellRate' ? 'Upsell' : m === 'greeting' ? 'Greeting' : 'Drinks'}
                </button>
              ))}
            </div>
          </div>

          <div className="text-xs text-gray-500 mb-3">{metricLabels[selectedMetric]}</div>

          {/* Simple bar chart */}
          <div className="flex items-end gap-2 h-40">
            {WEEKLY_STATS.map((week, i) => {
              const value = week[selectedMetric];
              const height = (value / (maxStat * 1.2)) * 100;
              const isLatest = i === WEEKLY_STATS.length - 1;
              return (
                <div key={week.week} className="flex-1 flex flex-col items-center gap-1">
                  <span className={`text-xs font-medium ${isLatest ? 'text-blue-600' : 'text-gray-500'}`}>
                    {value}{selectedMetric !== 'drinks' ? '%' : ''}
                  </span>
                  <div className="w-full relative" style={{ height: `${height}%` }}>
                    <div
                      className={`absolute inset-0 rounded-t-md transition-all duration-500 ${
                        isLatest ? 'bg-blue-500' : 'bg-blue-200'
                      }`}
                    />
                  </div>
                  <span className="text-[10px] text-gray-400">{week.week}</span>
                </div>
              );
            })}
          </div>

          <div className="mt-3 flex items-center gap-2 text-xs">
            <TrendingUp className="w-3.5 h-3.5 text-emerald-500" />
            <span className="text-emerald-600 font-medium">
              {selectedMetric === 'upsellRate' ? '+14% improvement over 6 weeks' :
               selectedMetric === 'greeting' ? '+10% improvement over 6 weeks' :
               '+100% more drink promos over 6 weeks'}
            </span>
          </div>
        </div>

        {/* Personal goals */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-gray-900">My Goals</h2>
            <span className="text-xs text-gray-400">Set your own targets</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {GOALS.map((goal, i) => {
              const pct = Math.min((goal.current / goal.target) * 100, 100);
              const isComplete = goal.current >= goal.target;
              return (
                <div key={i} className={`rounded-lg border p-4 ${isComplete ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <goal.icon className={`w-4 h-4 ${isComplete ? 'text-emerald-500' : 'text-gray-400'}`} />
                    <span className="text-sm font-medium text-gray-900">{goal.name}</span>
                  </div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${isComplete ? 'bg-emerald-400' : 'bg-blue-400'}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-gray-600 w-16 text-right">
                      {goal.current}{goal.unit} / {goal.target}{goal.unit}
                    </span>
                  </div>
                  <div className="text-[10px] text-gray-400">
                    {isComplete ? 'Goal reached! Set a new one?' : `${Math.round(pct)}% there — keep going!`}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Milestones */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Milestones</h2>
          <div className="space-y-3">
            {MILESTONES.map((m, i) => (
              <div key={i} className={`flex items-center gap-4 p-3 rounded-lg ${m.unlocked ? 'bg-gray-50' : 'opacity-50'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  m.unlocked ? 'bg-amber-100' : 'bg-gray-200'
                }`}>
                  {m.unlocked ? (
                    <m.icon className="w-5 h-5 text-amber-500" />
                  ) : (
                    <Lock className="w-4 h-4 text-gray-400" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">{m.name}</div>
                  <div className="text-xs text-gray-500">{m.description}</div>
                </div>
                <div className={`text-xs flex-shrink-0 ${m.unlocked ? 'text-gray-400' : 'text-gray-300'}`}>
                  {m.date}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Motivation */}
        <div className="text-center py-6">
          <p className="text-sm text-gray-400 italic">
            "I went from average to top 10% in 6 weeks. The stats don't lie — and neither does my paycheck."
          </p>
          <p className="text-xs text-gray-300 mt-1">— Marcus W., Gen Korean BBQ, LA</p>
        </div>
      </div>
    </div>
  );
};

export default MyStats;
